import { useEffect, useRef, useState } from 'react';
import { getAboutPage, getCompanySettings, getGallery } from '../services/api';

const defaultValues = [
  { icon: '⚙️', title: 'Precision Engineering', description: 'Every component is manufactured with meticulous attention to detail and strict quality control processes.' },
  { icon: '♻️', title: 'Sustainability', description: 'We are committed to environmentally responsible manufacturing practices and circular economy principles.' },
  { icon: '🤝', title: 'Customer Partnership', description: 'We build long-term relationships by understanding and exceeding our clients expectations consistently.' },
  { icon: '🚀', title: 'Innovation', description: 'Continuously investing in new technologies and processes to deliver better solutions faster.' },
];

const defaultGallery = [
  { src: 'https://images.unsplash.com/photo-1565891741441-64926e441838?w=800&q=80', alt: 'Manufacturing Floor' },
  { src: 'https://images.unsplash.com/photo-1504328345606-18bbc8c9d7d1?w=800&q=80', alt: 'Industrial Machinery' },
  { src: 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=800&q=80', alt: 'Engineering Team' },
  { src: 'https://images.unsplash.com/photo-1565793298595-6a879b1d9492?w=800&q=80', alt: 'Quality Control' },
  { src: 'https://images.unsplash.com/photo-1518709268805-4e9042af2176?w=800&q=80', alt: 'Production Line' },
  { src: 'https://images.unsplash.com/photo-1530124566582-a618bc2615dc?w=800&q=80', alt: 'Workshop' },
  { src: 'https://images.unsplash.com/photo-1504917595217-d4dc5ebe6122?w=800&q=80', alt: 'Assembly Process' },
  { src: 'https://images.unsplash.com/photo-1567789884554-0b844b597180?w=800&q=80', alt: 'Precision Tools' },
];

const About = () => {
  const sectionRef = useRef(null);
  const [data, setData] = useState({});
  const [settings, setSettings] = useState({});
  const [gallery, setGallery] = useState([]);

  useEffect(() => {
    getAboutPage().then(res => {
      if (res) setData(res);
    });
    getCompanySettings().then(res => {
      if (res) setSettings(res);
    });
    getGallery().then(res => {
      if (res && res.length > 0) {
        setGallery(res.map(g => ({ src: g.image, alt: g.caption || g.title })));
      }
    });
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('visible'); }),
      { threshold: 0.1, rootMargin: '0px 0px -30px 0px' }
    );
    if (sectionRef.current) {
      const els = sectionRef.current.querySelectorAll('.rv,.rl,.rr');
      els.forEach(el => observer.observe(el));
      return () => els.forEach(el => observer.unobserve(el));
    }
  }, [data]);

  const values = data.core_values && data.core_values.length > 0 ? data.core_values : defaultValues;

  return (
    <section id="about" ref={sectionRef}>
      <div className="wrap">
        <div className="about-layout">
          <div className="about-text rv">
            <div className="about-header">
              <div className="slide-index" style={{color:'var(--yd)'}}><span></span> {data.hero_subtitle || 'About Us'}</div>
              <h2 className="heading">
                Who We <em>Are</em>
              </h2>
            </div>
            <p className="about-lead">
              {data.description || 'We are a manufacturing company committed to innovation, precision, and sustainability — providing world-class engineering and remanufacturing solutions that drive industrial excellence.'}
            </p>
            <div className="about-body">
              <p>
                {data.who_we_are || 'Established with a mission to deliver reliable industrial solutions, we specialize in remanufacturing services and trading of top-quality machinery components. Our facilities are equipped with advanced technologies and certified systems that meet international standards.'}
              </p>
            </div>
          </div>
          <div className="about-image rv d2">
            <div className="about-img-wrap">
              {(data.facility_image || settings.about_image) ? (
                <img
                  src={data.facility_image || settings.about_image}
                  alt="PT CITRA HERITAGE INDONESIA Facility"
                  className="about-img"
                />
              ) : (
                <div className="about-img-placeholder">
                  <span role="img" aria-label="factory">🏭</span>
                </div>
              )}
              <div className="about-img-badge">
                <div className="aib-num">{data.stat_years || '15+'}</div>
                <div className="aib-label">Years of<br/>Excellence</div>
              </div>
            </div>
          </div>
        </div>

        {/* Gallery Slider */}
        <div className="about-gallery rv" style={{marginBottom:'80px'}}>
          <div className="slide-index" style={{color:'var(--yd)',justifyContent:'center'}}><span></span> Our Gallery</div>
          <h2 className="heading" style={{textAlign:'center',marginBottom:'40px'}}>
            Behind <em>The Scenes</em>
          </h2>
        </div>
        <div className="gallery-marquee-outer rv" style={{marginBottom:'80px'}}>
          <div className="gallery-marquee-track">
            {(() => {
              const items = gallery.length > 0 ? gallery : defaultGallery;
              return [...items, ...items].map((img, i) => (
                <div className="gallery-item" key={i}>
                  <img src={img.src} alt={img.alt} />
                  <div className="gallery-caption">{img.alt}</div>
                </div>
              ));
            })()}
          </div>
        </div>

        <div className="about-vm-grid">
          <div className="about-vm-card rv">
            <div className="vm-icon">🎯</div>
            <div className="vm-label">Our Mission</div>
            <p className="vm-text">
              {data.mission || 'To deliver reliable, high-quality components with fast lead time through innovation and sustainable practices, ensuring customer satisfaction and operational excellence.'}
            </p>
          </div>
          <div className="about-vm-card rv d2">
            <div className="vm-icon">🔭</div>
            <div className="vm-label">Our Vision</div>
            <p className="vm-text">
              {data.vision || 'To be a leading provider of remanufacturing and industrial solutions, recognized for our commitment to quality, sustainability, and technological advancement.'}
            </p>
          </div>
        </div>

        <div className="about-stats rv">
          <div className="about-stat">
            <div className="as-num">{data.stat_years || '15+'}</div>
            <div className="as-label">Years<br/>Experience</div>
          </div>
          <div className="about-stat">
            <div className="as-num">{data.stat_components || '500+'}</div>
            <div className="as-label">Components<br/>Delivered</div>
          </div>
          <div className="about-stat">
            <div className="as-num">{data.stat_clients || '120+'}</div>
            <div className="as-label">Satisfied<br/>Clients</div>
          </div>
          <div className="about-stat">
            <div className="as-num">ISO</div>
            <div className="as-label">{data.stat_certification || '9001:2015'}<br/>Certified</div>
          </div>
        </div>

        <div className="about-values rv">
          <div className="slide-index" style={{color:'var(--yd)'}}><span></span> Our Core Values</div>
          <h2 className="heading" style={{marginBottom:'40px'}}>What Drives Us <em>Forward</em></h2>
          <div className="values-grid">
            {values.map((val, i) => (
              <div className="value-card" key={i}>
                <span className="vc-num">{String(i + 1).padStart(2, '0')} ——</span>
                <span className="vc-icon">{val.icon}</span>
                <div className="vc-title">{val.title}</div>
                <p className="vc-desc">{val.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
